<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useMapAreaSelect } from '../model/model'
import './assets/style.scss'

const props = defineProps<{
  type: 'square' | 'circle'
}>()

const emits = defineEmits<{
  selected: [data: { type: 'bbox'; bbox: number[] } | { type: 'circle'; center: number[]; radius: number }]
}>()

const model = useMapAreaSelect()
model.emits = emits

const overlayEl = ref<HTMLElement | null>(null)
const currentScreen = ref([0, 0])

/**
 * Обновляет размер и позицию оверлея через requestAnimationFrame.
 * Координаты пересчитываются относительно родительского контейнера
 * (вычитается его смещение через getBoundingClientRect), чтобы оверлей
 * корректно клипировался overflow: hidden контейнера.
 * Для прямоугольника — top/left/width/height по двум угловым точкам.
 * Для окружности — точки начала и конца выделения задают диаметр.
 */
const updateOverlay = () => {
  if (!overlayEl.value || !model.isDrawing) return

  const containerRect = overlayEl.value.parentElement!.getBoundingClientRect()
  const sx = model.startScreenCoords[0] - containerRect.left
  const sy = model.startScreenCoords[1] - containerRect.top
  const cx = currentScreen.value[0] - containerRect.left
  const cy = currentScreen.value[1] - containerRect.top

  if (props.type === 'square') {
    overlayEl.value.style.left = Math.min(sx, cx) + 'px'
    overlayEl.value.style.top = Math.min(sy, cy) + 'px'
    overlayEl.value.style.width = Math.abs(cx - sx) + 'px'
    overlayEl.value.style.height = Math.abs(cy - sy) + 'px'
  } else {
    const r = Math.sqrt((cx - sx) ** 2 + (cy - sy) ** 2) / 2
    overlayEl.value.style.left = (sx + cx) / 2 - r + 'px'
    overlayEl.value.style.top = (sy + cy) / 2 - r + 'px'
    overlayEl.value.style.width = r * 2 + 'px'
    overlayEl.value.style.height = r * 2 + 'px'
  }
}

/**
 * Отслеживает перемещение мыши во время рисования области.
 * Обновляет текущие экранные координаты и запускает перерисовку оверлея.
 */
const handleMouseMove = (event: MouseEvent) => {
  if (!model.isDrawing) return
  currentScreen.value = [event.clientX, event.clientY]
  requestAnimationFrame(updateOverlay)
}

onMounted(() => {
  document.addEventListener('mousemove', handleMouseMove)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleMouseMove)
})
</script>

<template>
  <div v-if="model.isDrawing" ref="overlayEl" :class="['v-map-area-select', `v-map-area-select--${type}`]" />
</template>
